'use client';

import {
  ChangeEvent,
  FocusEvent,
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  useEffect,
  useId,
  useState,
} from 'react';
import classNames from 'classnames';

import { Label } from '../Label';
import { Error } from '../Error';

import './css/Checkbox.scss';

export interface CheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  checked?: boolean;
  error?: string;
  isErrorAnnounced?: boolean;
  label?: string;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

export const Checkbox = forwardRef(
  (props: CheckboxProps, ref: ForwardedRef<HTMLInputElement>) => {
    const {
      checked,
      className,
      disabled,
      error,
      id,
      isErrorAnnounced = true,
      label,
      name,
      onBlur: propOnBlur,
      onChange: propOnChange,
      onFocus: propOnFocus,
      onKeyUp: propOnKeyUp,
      value,
      ...spreadProps
    } = props;

    const customInputId = `${useId()}-checkbox`;
    const [isChecked, setIsChecked] = useState(checked || false);
    const inputId = id || customInputId;
    const svgMaskId = `${useId()}-svg-mask`;
    const errorId = `${inputId}-error`;

    const checkboxClass = classNames({
      'ps-checkbox': true,
      'ps-checkbox-disabled': disabled,
      'ps-checkbox-error': error,
      [`${className}`]: className,
    });

    useEffect(() => {
      setIsChecked(checked || false);
    }, [checked]);

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
      setIsChecked(!isChecked);
      if (propOnChange) propOnChange(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      (e.target as HTMLInputElement).parentElement?.classList.add(
        'ps-checkbox-focused'
      );
      if (propOnFocus) propOnFocus(e);
    };

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      (e.target as HTMLInputElement).parentElement?.classList.remove(
        'ps-checkbox-focused'
      );
      if (propOnBlur) propOnBlur(e);
    };

    return (
      <div className={checkboxClass}>
        <input
          {...spreadProps}
          checked={isChecked}
          disabled={disabled}
          id={inputId}
          name={name}
          onBlur={handleOnBlur}
          onChange={handleOnChange}
          onFocus={handleFocus}
          onKeyUp={propOnKeyUp}
          ref={ref}
          type="checkbox"
          value={value || label}
        />
        <svg
          className="ps-checkbox-check-svg"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 4 16 16"
          fill="none"
          focusable="false"
          aria-hidden="true"
        >
          <mask
            id={svgMaskId}
            fill="white"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M2 4C0.895431 4 0 4.89543 0 6V18C0 19.1046 0.895431 20 2 20H14C15.1046 20 16 19.1046 16 18V6C16 4.89543 15.1046 4 14 4H2ZM2.17573 12.4453L6.07573 16.8036C6.31004 17.0655 6.68994 17.0655 6.92426 16.8036L13.8243 9.09284C14.0586 8.83098 14.0586 8.40645 13.8243 8.14461L12.9758 7.19638C12.7415 6.93454 12.3615 6.93454 12.1272 7.19638L6.5 13.4848L3.87277 10.5489C3.63846 10.287 3.25854 10.287 3.02424 10.5489L2.17573 11.4971C1.94142 11.759 1.94142 12.1835 2.17573 12.4453Z"
            />
          </mask>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2 4C0.895431 4 0 4.89543 0 6V18C0 19.1046 0.895431 20 2 20H14C15.1046 20 16 19.1046 16 18V6C16 4.89543 15.1046 4 14 4H2ZM2.17573 12.4453L6.07573 16.8036C6.31004 17.0655 6.68994 17.0655 6.92426 16.8036L13.8243 9.09284C14.0586 8.83098 14.0586 8.40645 13.8243 8.14461L12.9758 7.19638C12.7415 6.93454 12.3615 6.93454 12.1272 7.19638L6.5 13.4848L3.87277 10.5489C3.63846 10.287 3.25854 10.287 3.02424 10.5489L2.17573 11.4971C1.94142 11.759 1.94142 12.1835 2.17573 12.4453Z"
            fill="#262626"
          />
          <path
            d="M6.07573 16.8036L6.82096 16.1368L6.82093 16.1368L6.07573 16.8036ZM2.17573 12.4453L2.92093 11.7785L2.92093 11.7785L2.17573 12.4453ZM6.92426 16.8036L7.66943 17.4705L7.66946 17.4705L6.92426 16.8036ZM13.8243 9.09284L14.5695 9.75969L14.5695 9.75965L13.8243 9.09284ZM13.8243 8.14461L13.0791 8.81144L13.0791 8.81145L13.8243 8.14461ZM12.9758 7.19638L13.721 6.52954L13.721 6.52953L12.9758 7.19638ZM12.1272 7.19638L12.8724 7.86322L12.8724 7.86322L12.1272 7.19638ZM6.5 13.4848L5.7548 14.1516L6.5 14.9844L7.24519 14.1516L6.5 13.4848ZM3.87277 10.5489L3.12754 11.2157L3.12757 11.2157L3.87277 10.5489ZM3.02424 10.5489L3.76944 11.2157L3.76947 11.2157L3.02424 10.5489ZM2.17573 11.4971L1.43052 10.8303L1.4305 10.8303L2.17573 11.4971ZM1 6C1 5.44772 1.44772 5 2 5V3C0.343146 3 -1 4.34315 -1 6H1ZM1 18V6H-1V18H1ZM2 19C1.44772 19 1 18.5523 1 18H-1C-1 19.6569 0.343146 21 2 21V19ZM14 19H2V21H14V19ZM15 18C15 18.5523 14.5523 19 14 19V21C15.6569 21 17 19.6569 17 18H15ZM15 6V18H17V6H15ZM14 5C14.5523 5 15 5.44772 15 6H17C17 4.34315 15.6569 3 14 3V5ZM2 5H14V3H2V5ZM6.82093 16.1368L2.92093 11.7785L1.43053 13.1122L5.33053 17.4704L6.82093 16.1368ZM6.1791 16.1367C6.24668 16.0612 6.36173 16 6.50001 16C6.6383 16 6.75338 16.0613 6.82096 16.1368L5.3305 17.4704C5.96232 18.1765 7.03761 18.1765 7.66943 17.4705L6.1791 16.1367ZM13.0791 8.426L6.17907 16.1368L7.66946 17.4705L14.5695 9.75969L13.0791 8.426ZM13.0791 8.81145C13.0164 8.74144 13 8.66854 13 8.61872C13 8.56889 13.0164 8.49602 13.079 8.42604L14.5695 9.75965C15.1435 9.11817 15.1435 8.11928 14.5695 7.47777L13.0791 8.81145ZM12.2306 7.86321L13.0791 8.81144L14.5695 7.47778L13.721 6.52954L12.2306 7.86321ZM12.8724 7.86322C12.8048 7.93877 12.6898 8 12.5515 8C12.4132 8 12.2982 7.93877 12.2306 7.86322L13.721 6.52953C13.0892 5.82349 12.0138 5.82349 11.382 6.52953L12.8724 7.86322ZM7.24519 14.1516L12.8724 7.86322L11.382 6.52953L5.7548 12.8179L7.24519 14.1516ZM3.12757 11.2157L5.7548 14.1516L7.24519 12.8179L4.61796 9.88203L3.12757 11.2157ZM3.76947 11.2157C3.70187 11.2912 3.58679 11.3525 3.4485 11.3525C3.31021 11.3525 3.19513 11.2912 3.12754 11.2157L4.618 9.88207C3.98619 9.17595 2.91082 9.17595 2.27901 9.88207L3.76947 11.2157ZM2.92094 12.1639L3.76944 11.2157L2.27903 9.88205L1.43052 10.8303L2.92094 12.1639ZM2.92093 11.7785C2.98358 11.8485 3 11.9214 3 11.9712C3 12.0211 2.98358 12.0939 2.92096 12.1639L1.4305 10.8303C0.856518 11.4718 0.856472 12.4707 1.43053 13.1122L2.92093 11.7785Z"
            fill="#262626"
            mask={`url(#${svgMaskId})`}
          />
        </svg>

        <Label
          disabled={disabled}
          htmlFor={inputId}
          isBold={false}
        >
          {label}
        </Label>
        {error && (
          <Error
            id={errorId}
            isErrorAnnounced={isErrorAnnounced}
          >
            {error}
          </Error>
        )}
      </div>
    );
  }
);
